/**
 * THE HERD — closed-form motion, evaluated identically by the server and by
 * every client.
 *
 * ── The architectural decision this file is ─────────────────────────────────
 * A deer has no simulation state. Its entire life is a pure function of the
 * spawn record and the clock:
 *
 *     poseAt(spawn, tServerMs) -> { sx, sy, scale, ... }
 *
 * The server broadcasts a spawn ONCE and thereafter sends only outcomes —
 * "killed by seat 3", "escaped". It never streams positions. Four things fall
 * out of that, and the panel wanted all four (ROUNDTABLE.md, A-9):
 *
 *   1. Bandwidth per saloon is a few hundred bytes per wave. An idle saloon
 *      costs a heartbeat. The droplet hosts five other sites; this matters.
 *   2. Lag compensation is exact and O(1). Rewinding a shot 140 ms is calling
 *      `poseAt` with a smaller `t`. There is no history ring buffer, and no
 *      resolution loss from having sampled at 20 Hz.
 *   3. Clients render at their own frame rate with no interpolation artefacts,
 *      because they are not interpolating between snapshots — they are
 *      evaluating the same function the server evaluates.
 *   4. A client that stalls for 400 ms comes back *correct*, not behind. There
 *      is nothing to catch up on.
 *
 * The cost is that motion must be expressible in closed form. That rules out
 * deer that react to being shot at, and the design accepted that: an arcade
 * herd runs its pattern regardless, which is exactly how the genre plays.
 *
 * ── Determinism ─────────────────────────────────────────────────────────────
 * `poseAt` must agree between engines. It uses only `+ - * /`, `Math.sin`,
 * `Math.pow` and `Math.abs` on values derived from the spawn record. `Math.sin`
 * is not bit-identical across engines in principle, but the divergence is at
 * the ULP and the hit test's tolerances are four orders of magnitude larger —
 * and, critically, the SERVER's evaluation is the only one that decides a hit.
 * A client's evaluation only decides what it draws.
 */

import { clamp, clamp01, depthScale, groundSy } from './stage.js';

// ── kinds ───────────────────────────────────────────────────────────────────

export type DeerKind = 'buck' | 'doe';

/** Antler points. The headline number on a trophy, and the base of the score. */
export const RACK_SIZES = [6, 8, 10, 12, 14] as const;
export type RackSize = (typeof RACK_SIZES)[number];

export const RACK_BASE: Record<RackSize, number> = {
  6: 400,
  8: 700,
  10: 1100,
  12: 1700,
  14: 2500,
};

/** The once-a-hunt legendary buck. Not in `RACK_SIZES`; it is its own event. */
export const LEGEND_RACK = 20;
export const LEGEND_BASE = 6000;

// ── the spawn record — this is what goes on the wire ────────────────────────

export interface DeerSpawn {
  /** Unique within a wave. */
  id: number;
  kind: DeerKind;
  /** Antler points; 0 for a doe. */
  rack: number;
  /** Server time (ms) at which t' = 0. */
  t0: number;
  /** Server time (ms) at which the deer has left the stage for good. */
  tExit: number;
  /** Starting world x. Outside the stage, so the deer walks on. */
  x0: number;
  /** Starting depth. */
  z0: number;
  /** World x velocity, units/second. Sign is the direction of travel. */
  vx: number;
  /** Depth drift, units/second. Small; a deer angling toward or away. */
  vz: number;
  /** Gait frequency, Hz — the bob, and the leap when `leap` > 0. */
  gait: number;
  /** Vertical bounce amplitude, in deer-heights. */
  bob: number;
  /** Leap height in deer-heights. 0 for a walker. */
  leap: number;
  /** Lateral weave amplitude in world units. */
  weave: number;
  /** Lateral weave frequency, Hz. */
  weaveHz: number;
  /** Speed-warp amplitude, seconds. Makes a deer surge and hesitate. */
  warp: number;
  /** Speed-warp frequency, Hz. */
  warpHz: number;
  /** Phase offset so two deer with identical parameters are not in lockstep. */
  phase: number;
}

/** Where a deer is, and how big, at a moment. */
export interface DeerPose {
  /** Stage x of the hooves. */
  sx: number;
  /** Stage y of the hooves. */
  sy: number;
  /** Depth, after drift. */
  z: number;
  /** Size multiplier for the whole animal. */
  scale: number;
  /** +1 facing right, −1 facing left. */
  dir: number;
  /** 0..1 through the gait cycle, for the renderer's legs. */
  gaitPhase: number;
  /** How far off the ground the body is, in stage units (a leap). */
  lift: number;
}

/**
 * The one function. Everything else in the game is downstream of it.
 *
 * `t` is SERVER time in ms. A client passes `serverNow()` from `net/clock.ts`;
 * it must never pass `Date.now()` directly, or its herd runs at its own clock's
 * offset and it will aim at deer the server does not think are there.
 */
export function poseAt(d: DeerSpawn, t: number): DeerPose {
  const tp = (t - d.t0) / 1000;

  // Speed warp: a smooth surge-and-hesitate applied to the time parameter
  // rather than to the velocity, so the path stays a closed form and the deer
  // never teleports. `warp` is bounded at spawn so `u` stays monotonic.
  const u = tp + d.warp * Math.sin(tp * d.warpHz * TAU + d.phase);

  const z = clamp(d.z0 + d.vz * u, 0.08, 1.0);
  const scale = depthScale(z);

  // Lateral weave is in world units, so a distant weaving deer weaves less on
  // screen — which is what depth means.
  const x = d.x0 + d.vx * u + d.weave * Math.sin(u * d.weaveHz * TAU + d.phase * 1.7);
  const sx = x * scale * WORLD_TO_STAGE;

  const gaitPhase = (u * d.gait) % 1;
  // `abs(sin)` rather than `sin`: a leap is a series of arcs above the ground,
  // never below it.
  const arc = Math.abs(Math.sin(u * d.gait * Math.PI));
  const lift = (d.bob * arc + d.leap * arc * arc) * scale;

  return {
    sx,
    sy: groundSy(z) - lift,
    z,
    scale,
    dir: d.vx >= 0 ? 1 : -1,
    gaitPhase: gaitPhase < 0 ? gaitPhase + 1 : gaitPhase,
    lift,
  };
}

const TAU = Math.PI * 2;

/**
 * World x is scaled by depth before it becomes stage x, so this constant sets
 * how much stage a deer crosses per world unit at z = 0.
 */
const WORLD_TO_STAGE = 1.9;

// ── hit testing ─────────────────────────────────────────────────────────────

export type HitZone = 'vitals' | 'head' | 'body';

/** Score multiplier by where the shot landed. */
export const ZONE_MULT: Record<HitZone, number> = {
  vitals: 1.6,
  head: 1.35,
  body: 1.0,
};

export const ZONE_LABEL: Record<HitZone, string> = {
  vitals: 'CLEAN KILL',
  head: 'HEAD SHOT',
  body: 'DOWNED',
};

/**
 * Deer anatomy in local units, where 1.0 is the distance from hoof to shoulder.
 * The renderer draws from these same numbers, which is the only way the hitbox
 * and the animal a player is looking at can be guaranteed to agree.
 *
 * `dx` is measured along the direction of travel, so it is mirrored for a deer
 * running left. `dy` is negative upward from the hooves.
 */
export interface Zone {
  /** Along the direction of travel, mirrored for a deer running left. */
  readonly dx: number;
  /** Negative is upward from the hooves. */
  readonly dy: number;
  readonly rx: number;
  readonly ry: number;
}

export const ANATOMY: Record<'body' | 'vitals' | 'head', Zone> = {
  body: { dx: -0.02, dy: -0.62, rx: 0.44, ry: 0.25 },
  /** Behind the shoulder. The shot a hunter is actually trying to make. */
  vitals: { dx: 0.13, dy: -0.62, rx: 0.15, ry: 0.15 },
  head: { dx: 0.5, dy: -0.95, rx: 0.19, ry: 0.15 },
};

/**
 * Test a shot against a deer. Returns the zone hit, or null for a miss.
 *
 * Zones are tested tightest-first, so a shot inside the vitals is never
 * reported as a body hit. `pad` widens every ellipse — the server grants a
 * small allowance because the player is aiming at a moving animal through a
 * crosshair that is itself a few pixels wide, and a game that demands
 * sub-pixel truth is not fun. It is a constant, applied to everyone equally.
 */
export function hitTest(d: DeerSpawn, t: number, sx: number, sy: number, pad = HIT_PAD): HitZone | null {
  const p = poseAt(d, t);
  const s = p.scale;
  if (Math.abs(sx - p.sx) > (ANATOMY.head.dx + 0.5) * s + pad) return null;

  const zones: Array<[HitZone, Zone]> = [
    ['vitals', ANATOMY.vitals],
    ['head', ANATOMY.head],
    ['body', ANATOMY.body],
  ];
  for (const [zone, a] of zones) {
    const cx = p.sx + a.dx * p.dir * s;
    const cy = p.sy + a.dy * s;
    const rx = a.rx * s + pad;
    const ry = a.ry * s + pad;
    const nx = (sx - cx) / rx;
    const ny = (sy - cy) / ry;
    if (nx * nx + ny * ny <= 1) return zone;
  }
  return null;
}

/**
 * Aim allowance, in stage units. ~1.2% of stage width. Chosen so a deer at the
 * treeline — which is genuinely small — is still hittable by a human with a
 * shaking phone, without making a near deer trivial.
 */
export const HIT_PAD = 0.012;

// ── spawning ────────────────────────────────────────────────────────────────

export interface DeerRecipe {
  kind: DeerKind;
  rack: number;
  /** World units per second, absolute. */
  speed: number;
  /** Depth band the deer runs in. */
  zMin: number;
  zMax: number;
  leap: number;
  weave: number;
  /** Seconds after the wave starts. */
  delay: number;
}

/**
 * Turn a recipe plus a seed into a spawn record with a computed exit time.
 *
 * `tExit` is solved once here rather than checked every tick. The path is not
 * analytically invertible once weave and warp are on it, so it is found by
 * forward sampling at 50 ms — thirty microseconds of arithmetic, once per deer,
 * against a per-tick bounds check for every deer in every saloon forever.
 */
export function makeSpawn(id: number, r: DeerRecipe, waveStart: number, rng: import('./rng.js').Rng): DeerSpawn {
  const dir = rng.sign();
  const z0 = rng.range(r.zMin, r.zMax);
  const speed = r.speed * rng.range(0.88, 1.12);

  // Start outside the stage so the deer walks on rather than materialising.
  const entrySx = 1.16;
  const x0 = (-dir * entrySx) / (depthScale(z0) * WORLD_TO_STAGE);

  const spawn: DeerSpawn = {
    id,
    kind: r.kind,
    rack: r.rack,
    t0: waveStart + r.delay * 1000,
    tExit: 0,
    x0,
    z0,
    vx: dir * speed,
    // A deer that only ever runs parallel to the treeline reads as a cardboard
    // cutout on a rail. A little drift makes it an animal crossing ground.
    vz: rng.range(-0.035, 0.035),
    gait: rng.range(1.5, 2.2) * (r.leap > 0 ? 0.75 : 1),
    bob: r.leap > 0 ? 0.05 : rng.range(0.02, 0.05),
    leap: r.leap,
    // Bounded below, after weaveHz is known.
    weave: 0,
    weaveHz: rng.range(0.25, 0.55),
    // Bounded so `u` stays monotonic: |d(warp·sin(2π·f·t))/dt| < 1 requires
    // warp·2π·f < 1. Violating that would run the deer backwards mid-stride.
    warp: 0,
    warpHz: rng.range(0.3, 0.7),
    phase: rng.range(0, TAU),
  };
  const maxWarp = 0.85 / (TAU * spawn.warpHz);
  spawn.warp = rng.range(0, maxWarp);

  /**
   * Bound the weave by the deer's own forward speed.
   *
   * The weave term contributes a lateral velocity of `weave · weaveHz · 2π`.
   * Left unbounded that can exceed `vx` outright — which looked like an animal
   * snapping sideways rather than running a line, and showed up as a hard
   * number in `npm run check:herd`: it was what pushed the worst-case
   * lag-compensation displacement to 1.61 body lengths, over the design bar,
   * even after the depth bands were fixed.
   *
   * A third of forward speed is about what a deer actually does when it jinks.
   */
  const wantWeave = r.weave * rng.range(0.7, 1.3);
  const maxWeave = (0.35 * Math.abs(spawn.vx)) / (TAU * spawn.weaveHz);
  spawn.weave = Math.min(wantWeave, maxWeave);

  spawn.tExit = solveExit(spawn);
  return spawn;
}

function solveExit(d: DeerSpawn): number {
  const STEP = 50;
  const LIMIT = 40_000;
  for (let dt = 0; dt <= LIMIT; dt += STEP) {
    const p = poseAt(d, d.t0 + dt);
    // Must have entered before it can be considered to have left, or a deer
    // starting outside the stage exits on its first sample.
    if (dt > 400 && Math.abs(p.sx) > 1.28) return d.t0 + dt;
  }
  return d.t0 + LIMIT;
}

/** Is this deer on stage and still alive at `t`? */
export function isLive(d: DeerSpawn, t: number): boolean {
  return t >= d.t0 && t < d.tExit;
}

export function rackFor(rng: import('./rng.js').Rng, tier: number): RackSize {
  // Bigger racks get likelier as the hunt goes on, but a 14 is never guaranteed
  // and a 6 is never off the table — the variance is what makes a big one feel
  // like something that happened to you.
  const roll = rng.next() + clamp01(tier) * 0.42;
  if (roll > 1.24) return 14;
  if (roll > 1.0) return 12;
  if (roll > 0.72) return 10;
  if (roll > 0.4) return 8;
  return 6;
}

/** How fast is this deer moving, 0..1, for the speed bonus. */
export function speedFactor(d: DeerSpawn): number {
  return clamp01((Math.abs(d.vx) - 0.35) / 0.75);
}
